/**
 * THE ORGANISATION-WIDE AUTOMATION RATIO — one figure over many Roles, and the
 * denominator that makes it honest.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THIS IS THE FIGURE THAT WAS REFUSED, AND THIS FILE IS WHY IT IS NOW SERVED
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The reference screen's own source refused to derive an organisation-wide
 * automated figure and shipped a hardcoded literal instead, stating the reason
 * in a comment: the Roles differ in size and in how much work they carry, so an
 * unweighted mean of the per-Role column would be a wrong number that looks
 * right.
 *
 * That refusal was correct about the MEAN and it is not an argument against a
 * figure. The mean is wrong because it weights a two-person Role and a
 * forty-person Role equally. The fix is to weight by the thing that was named:
 * how much work each Role carries.
 *
 * ── THE WEIGHTING, AND WHY IT IS THE ONLY DEFENSIBLE ONE HERE ───────────────
 *
 *     ratio = sum(impactPersonHours) / sum(workloadPersonHours)
 *
 * WEIGHTED BY WORKLOAD PERSON-HOURS A YEAR — the quantity of work a Role
 * carries, which is exactly the quantity the refusal named. Written as a
 * weighted mean of the per-Role ratios it is `sum(w_i · r_i) / sum(w_i)` with
 * `w_i = workloadPersonHours_i`, and because `r_i = impact_i / workload_i` the
 * two forms are the same number. So this is not a weighting invented to produce
 * a figure; it is the definition of "what share of this organisation's work its
 * automation removes", and the unweighted mean is the approximation to it that
 * happens to be wrong.
 *
 * A two-person Role at 90% and a forty-person Role at 5% do not average to
 * 47.5% of the organisation's work. They are 9% of it.
 *
 * Three candidate weights were rejected and each rejection is a fact about the
 * schema rather than a preference:
 *
 * - HEADCOUNT — a member's seat (`RoleGroupMember`) carries no hours and no
 *   cost, so a member is not a unit of work. Two Roles with five members each can differ by 10× in hours.
 * - SYSTEMS — `RoleResource` counts what is attached, not what is done. It is
 *   the numerator's population, so weighting by it would let attaching a system
 *   move the denominator.
 * - WORKLOAD COST — real, but it is money, and mixing a money weight into an
 *   hours ratio gives a figure whose units nobody can state. Cost belongs to the
 *   impact figure, where it already is.
 *
 * ── THE COUNT IS PART OF THE ANSWER. IT IS NOT METADATA BESIDE IT ───────────
 *
 * "62% of the work is automated" and "62% across 3 of 11 Roles" are different
 * claims and only the second is true. Per-Role coverage comes back
 * `not-modelled` routinely — that is the designed answer for a Role nobody has
 * authored yet — so any organisation-wide figure describes WHICHEVER SUBSET
 * happened to have a model.
 *
 * So the counts live INSIDE the union arms rather than as sibling fields. That
 * is the whole design: a client destructuring `{ ratio }` cannot get one without
 * also having `rolesModelled` and `rolesTotal` in hand. Sibling fields would be
 * equally present and trivially ignorable, and the failure mode is a stat card
 * rendering a confident percentage over three Roles out of eleven.
 *
 * ── WHY THERE IS NO MINIMUM-COVERAGE THRESHOLD ─────────────────────────────
 *
 * There is deliberately NO fraction of modelled Roles below which this refuses
 * to answer. A threshold would suppress a true measurement over the Roles that
 * ARE modelled, and it would be a magic number nobody could defend — why 50%
 * and not 40%.
 *
 * The dishonesty was never in the arithmetic. It is in rendering the ratio
 * without its denominator, and that is fixed structurally above.
 *
 * ── WHAT COUNTS AS INCLUDED ────────────────────────────────────────────────
 *
 * A Role contributes to BOTH sums or to NEITHER. Only a Role whose own coverage
 * is `modelled` is included — it has a workload that evaluates to a strictly
 * positive, finite number of person-hours. A Role with impact models and no
 * usable workload would otherwise add automated hours to the numerator with
 * nothing under them, inflating the organisation's figure by exactly the amount
 * nobody modelled.
 *
 * 🚨 AN ABSENT ROLE IS NEVER A ZERO ON EITHER SIDE. It is excluded from both
 * sums and counted in `rolesTotal - rolesModelled`. Contributing `0` to the
 * numerator alone would understate; contributing `0` to both would leave the
 * ratio unchanged while making `rolesModelled` a lie.
 *
 * A Role that IS modelled and automates nothing passes: it contributes `0` to
 * the numerator and its full workload to the denominator. That is a real
 * measurement and it belongs in the answer.
 */
import { z } from "zod";

/**
 * Why the organisation has no ratio at all.
 *
 * Four arms, separate because they send a reader to four different places.
 * Collapsing them would be the `NO_WORKING_TIME_MODEL` mistake at organisation
 * scale: an admin sent to audit eleven Roles when the fix is one settings form.
 */
export const ORG_COVERAGE_NOT_MODELLED_REASONS = [
  /**
   * The organisation holds no Roles at all. Nothing is wrong; there is nothing
   * to measure yet. Distinct from having Roles nobody has modelled, because the
   * next action is "create a Role", not "author a workload".
   */
  "NO_ROLES",
  /**
   * The organisation never stated its working day, week and year, so no
   * quantity in any model has a length and EVERY Role is unmeasurable for one
   * shared reason.
   *
   * 🚨 THIS IS WHY IT IS NOT REPORTED AS "0 of 11 modelled". That count is
   * arithmetically true and sends an admin hunting through eleven Roles for a
   * fault that is in one org-level form. One cause, one sentence, one fix.
   */
  "NO_WORKING_TIME_MODEL",
  /**
   * Roles exist and the working-time model exists, but not one Role has a
   * workload that evaluates to a usable denominator. The counts travel with it,
   * so `0 of 11` is what a reader sees.
   */
  "NO_MODELLED_ROLES",
  /**
   * Roles WERE summed and the quotient is not a finite number.
   *
   * 🚨 THIS ARM EXISTS SO THE REASON CANNOT CONTRADICT THE COUNT BESIDE IT.
   * Reporting this as `NO_MODELLED_ROLES` while `rolesModelled` is 4 is a
   * payload that disagrees with itself, and a reader who trusts the reason and
   * a reader who trusts the count draw opposite conclusions. Distinct name,
   * matching the per-Role engine's own `RATIO_NOT_FINITE`.
   */
  "RATIO_NOT_FINITE"
] as const;

export type OrgCoverageNotModelledReason = (typeof ORG_COVERAGE_NOT_MODELLED_REASONS)[number];

export const OrgCoverageNotModelledReasonSchema = z.enum(ORG_COVERAGE_NOT_MODELLED_REASONS);

/**
 * How much of the organisation the figure actually covers.
 *
 * Present on BOTH arms. On the modelled arm it is the qualifier the percentage
 * cannot be rendered without; on the not-modelled arm it is the difference
 * between "you have no Roles" and "you have eleven and have modelled none".
 */
const OrgCoverageScopeShape = {
  /** Roles whose own coverage evaluated to a usable figure and were summed. */
  rolesModelled: z.number().int().min(0),
  /** Every Role the organisation holds, modelled or not. */
  rolesTotal: z.number().int().min(0)
};

/**
 * The organisation's automation ratio, or a named statement that there is none.
 *
 * `ratio` is a FRACTION, not a percentage — `0.1828`, never `18.28` — matching
 * `CoverageRatio` exactly so a reader who knows one knows the other.
 *
 * Both sums travel with it. That is not diagnostics: they ARE the weighting,
 * published so a reader can check the figure rather than trust it, and so the
 * hours behind the percentage never have to be re-derived from a rounded one.
 */
export const OrgCoverageRatioSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("modelled"),
    ratio: z.number(),
    /** Person-hours a year the modelled Roles' systems remove. The numerator. */
    impactPersonHours: z.number(),
    /** Person-hours a year the modelled Roles work. The denominator, and the weight. */
    workloadPersonHours: z.number(),
    ...OrgCoverageScopeShape
  }),
  z.strictObject({
    kind: z.literal("not-modelled"),
    reason: OrgCoverageNotModelledReasonSchema,
    ...OrgCoverageScopeShape
  })
]);

/**
 * The SPREAD of the per-Role ratios — the honest sibling of the weighted figure.
 *
 * A genuine minimum and maximum over the SAME population the ratio summed, so
 * "42% overall, 12–74% by Role" describes one set of Roles twice rather than two
 * different sets once. Served rather than derived client-side for that reason
 * alone: the inclusion rule lives in one place or it drifts.
 */
export const OrgCoverageSpreadSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("measured"),
    /** Lowest per-Role ratio among the modelled Roles. A fraction. */
    min: z.number(),
    /** Highest per-Role ratio among the modelled Roles. A fraction. */
    max: z.number(),
    ...OrgCoverageScopeShape
  }),
  z.strictObject({
    kind: z.literal("unavailable"),
    /**
     * The SAME vocabulary as the ratio's, resolved the same way.
     *
     * 🚨 IT WAS ONCE A LONE `NO_MODELLED_ROLES` LITERAL, AND THAT MADE ONE CARD
     * CONTRADICT ITSELF: an organisation with no Roles rendered "you have no
     * Roles" as its headline and "no modelled Roles" as its sub-label. Two
     * figures over one population owe a reader ONE explanation of why neither
     * is there.
     */
    reason: OrgCoverageNotModelledReasonSchema,
    ...OrgCoverageScopeShape
  })
]);

/** The organisation's automation ratio, or a named absence. */
export type OrgCoverageRatio = z.infer<typeof OrgCoverageRatioSchema>;

/** The range of per-Role ratios behind the organisation's figure. */
export type OrgCoverageSpread = z.infer<typeof OrgCoverageSpreadSchema>;
